import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useSection } from "../lib/useCMS";
import { trackConversion, EVENTS } from "../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FALLBACK = {
  overline: "The List",
  heading_top: "Get on the",
  heading_accent: "Inside.",
  description: "Early access to clinics, roster drops, defensemen drills, and training notes from Coach Mike. No spam. Hockey only.",
  consent_text: "By signing up, you agree to receive training updates from Built Different Hockey. Unsubscribe anytime.",
  submit_label: "Join the List",
  success_title: "You're on the list.",
  success_body: "Check your inbox. Details, drills, and session drops are coming your way.",
};

export const Newsletter = () => {
  const n = useSection("newsletter", FALLBACK);
  const [form, setForm] = useState({ name: "", email: "", player_level: "", website: "" });
  const [state, setState] = useState("idle");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setState("loading");
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        name: form.name.trim(),
        email: form.email.trim(),
        player_level: form.player_level || null,
        website: form.website,
      });
      setState("success");
      trackConversion(EVENTS.NEWSLETTER_SIGNUP, {
        method: "newsletter_section",
        player_level: form.player_level || undefined,
      });
      toast.success("You're on the list. Welcome to BDH.");
    } catch (err) {
      setState("idle");
      const msg = err?.response?.data?.detail || "Something went wrong. Try again in a moment.";
      toast.error(msg);
    }
  };

  return (
    <section id="newsletter" data-testid="newsletter-section" className="relative py-24 md:py-32 bg-black border-t border-white/5 overflow-hidden">
      <div className="absolute -top-20 -right-20 h-[460px] w-[460px] bg-brand-light/20 blur-3xl pointer-events-none" />

      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-x-0 gap-y-10 md:gap-10">
          <div className="col-span-12 md:col-span-5">
            <p className="overline">{n.overline}</p>
            <h2 className="font-display text-5xl md:text-6xl uppercase mt-5 leading-[0.9]" data-testid="newsletter-heading">
              {n.heading_top}
              <br />
              <span className="text-brand-light">{n.heading_accent}</span>
            </h2>
            <p className="mt-6 text-zinc-400 max-w-md">{n.description}</p>
          </div>

          <motion.form initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} onSubmit={onSubmit} className="col-span-12 md:col-span-7 bento-card p-6 md:p-10" data-testid="newsletter-form">
            {state === "success" ? (
              <div className="flex flex-col items-center justify-center text-center py-12" data-testid="newsletter-success">
                <div className="h-16 w-16 bg-brand grid place-items-center mb-6">
                  <Check className="h-8 w-8 text-white" />
                </div>
                <div className="font-display text-4xl md:text-5xl uppercase leading-[0.9]">{n.success_title}</div>
                <p className="text-zinc-400 mt-4 max-w-sm">{n.success_body}</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Honeypot — hidden from humans, visible to bots. Must stay empty. */}
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    className="hidden"
                    aria-hidden="true"
                  />
                  <label className="flex flex-col gap-2">
                    <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400">Name</span>
                    <input type="text" name="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" data-testid="newsletter-name-input" className="bg-transparent border-b-2 border-white/15 focus:border-brand-light outline-none py-3 text-white placeholder:text-zinc-600 transition-colors" required />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400">Email</span>
                    <input type="email" name="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@domain.com" data-testid="newsletter-email-input" className="bg-transparent border-b-2 border-white/15 focus:border-brand-light outline-none py-3 text-white placeholder:text-zinc-600 transition-colors" required />
                  </label>
                  <label className="flex flex-col gap-2 md:col-span-2">
                    <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400">Player Level (optional)</span>
                    <input type="text" name="player_level" value={form.player_level} onChange={(e) => setForm({ ...form, player_level: e.target.value })} placeholder="e.g. 14U AAA, High School, Junior, Parent" data-testid="newsletter-level-input" className="bg-transparent border-b-2 border-white/15 focus:border-brand-light outline-none py-3 text-white placeholder:text-zinc-600 transition-colors" />
                  </label>
                </div>

                <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                  <p className="text-xs text-zinc-500 max-w-sm">{n.consent_text}</p>
                  <button type="submit" disabled={state === "loading"} data-testid="newsletter-submit-button" className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed">
                    {state === "loading" ? (<><Loader2 className="h-5 w-5 animate-spin" />Joining...</>) : (<>{n.submit_label} <ArrowUpRight className="h-5 w-5" /></>)}
                  </button>
                </div>
              </>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
};
